import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Send, MessageSquare, Image, FileText, 
  Clock, CheckCircle, AlertCircle, TrendingUp, User,
  Calendar, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerProjectDetail = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('comment');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  const loadProjectData = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/interaction-summary`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load project data');
      navigate('/portal');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/projects/${projectId}/messages`, {
        project_id: projectId,
        message: newMessage,
        message_type: messageType,
        priority: 'normal',
        status: 'open'
      });
      
      toast.success('Message sent successfully!');
      setNewMessage('');
      loadProjectData();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'question': return <AlertCircle size={16} className="text-blue-500" />;
      case 'issue': return <AlertCircle size={16} className="text-red-500" />;
      case 'update': return <Info size={16} className="text-green-500" />;
      default: return <MessageSquare size={16} className="text-muted-foreground" />;
    }
  };

  const getUpdateTypeIcon = (type) => {
    switch (type) {
      case 'milestone': return <CheckCircle size={18} className="text-green-500" />;
      case 'photo': return <Image size={18} className="text-blue-500" />;
      case 'document': return <FileText size={18} className="text-purple-500" />;
      case 'progress': return <TrendingUp size={18} className="text-primary" />;
      default: return <Info size={18} className="text-muted-foreground" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/portal')}
            className="mb-4 font-mono uppercase text-xs"
          >
            <ArrowLeft size={14} className="mr-2" />
            Back to Portal
          </Button>
          
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
            PROJECT DETAILS
          </h1>
          <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
            {data.project.project_name}
          </p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-border">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Messages</p>
              <p className="text-2xl font-bold font-mono text-blue-500">
                {data.statistics.total_messages}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {data.statistics.open_messages} open
              </p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Updates</p>
              <p className="text-2xl font-bold font-mono text-green-500">
                {data.statistics.total_updates}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Your Messages</p>
              <p className="text-2xl font-bold font-mono text-primary">
                {data.statistics.customer_messages}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Staff Replies</p>
              <p className="text-2xl font-bold font-mono text-purple-500">
                {data.statistics.staff_messages}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Messages */}
          <div className="lg:col-span-2 space-y-6">
            {/* New Message */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl font-semibold uppercase flex items-center gap-2">
                  <Send size={20} />
                  Send Message
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div>
                    <select
                      value={messageType}
                      onChange={(e) => setMessageType(e.target.value)}
                      className="w-full px-3 py-2 bg-background border border-input rounded-sm font-mono text-sm mb-3"
                    >
                      <option value="comment">General Comment</option>
                      <option value="question">Question</option>
                      <option value="issue">Report Issue</option>
                      <option value="update">Status Update Request</option>
                    </select>
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message here..."
                      className="min-h-[100px]"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase"
                  >
                    <Send size={16} className="mr-2" />
                    {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Messages Thread */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl font-semibold uppercase flex items-center gap-2">
                  <MessageSquare size={20} />
                  Conversation ({data.messages.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.messages.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No messages yet. Start the conversation!
                  </p>
                ) : (
                  <div className="space-y-4">
                    {data.messages.map((message) => {
                      const isCustomer = message.sender_role === 'Customer';
                      const isYou = message.sender_id === user.id;
                      
                      return (
                        <div
                          key={message.id}
                          className={`p-4 rounded-sm border ${
                            isCustomer
                              ? 'bg-primary/5 border-primary/20 ml-0 mr-8'
                              : 'bg-accent/50 border-border ml-8 mr-0'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getMessageTypeIcon(message.message_type)}
                              <span className="font-semibold text-sm">
                                {isYou ? 'You' : message.sender_name}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                                isCustomer
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-purple-500/20 text-purple-400'
                              }`}>
                                {message.sender_role}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">
                              {formatDate(message.created_at)}
                            </div>
                          </div>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{message.message}</p>
                          
                          {message.status && (
                            <div className="mt-3 pt-3 border-t border-border">
                              <span className={`text-xs font-mono uppercase px-2 py-1 rounded ${
                                message.status === 'resolved'
                                  ? 'bg-green-500/20 text-green-400'
                                  : message.status === 'in_progress'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-gray-500/20 text-gray-400'
                              }`}>
                                {message.status}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Project Updates */}
          <div className="space-y-6">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg font-semibold uppercase">Project Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Project Name</p>
                  <p className="font-medium">{data.project.project_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Status</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-mono uppercase ${
                    data.project.status === 'Completed'
                      ? 'bg-green-500/20 text-green-400'
                      : data.project.status === 'In Progress'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {data.project.status}
                  </span>
                </div>
                {data.project.start_date && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Start Date</p>
                    <p className="font-mono">{new Date(data.project.start_date).toLocaleDateString()}</p>
                  </div>
                )}
                {data.project.end_date && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Target Completion</p>
                    <p className="font-mono">{new Date(data.project.end_date).toLocaleDateString()}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg font-semibold uppercase flex items-center gap-2">
                  <TrendingUp size={18} />
                  Project Updates
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.updates.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No updates yet</p>
                ) : (
                  <div className="space-y-3">
                    {data.updates.slice(0, 5).map((update) => (
                      <div key={update.id} className="p-3 bg-accent/30 rounded-sm border border-border">
                        <div className="flex items-start gap-2 mb-2">
                          {getUpdateTypeIcon(update.update_type)}
                          <div className="flex-1">
                            <p className="font-semibold text-sm">{update.title}</p>
                            <p className="text-xs text-muted-foreground">{update.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                          <span className="text-xs text-muted-foreground">{update.created_by_name}</span>
                          <span className="text-xs text-muted-foreground font-mono">
                            {formatDate(update.created_at)}
                          </span>
                        </div>
                        {update.progress_percentage !== null && (
                          <div className="mt-2">
                            <div className="w-full bg-border rounded-full h-2">
                              <div 
                                className="bg-primary rounded-full h-2 transition-all"
                                style={{ width: `${update.progress_percentage}%` }}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 text-right">
                              {update.progress_percentage}% complete
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProjectDetail;
